import * as React from 'react';

import {
  DimeDropdownMenuItem,
  DimeDropdownMenuSub,
  DimeDropdownMenuSubTrigger,
} from '../DimeDropdownMenu.js';
import { DisabledTooltip } from '../DisabledTooltip.js';
import { isToolbarItemAllowed } from '../lib/is-toolbar-item-allowed.js';
import { MenuItemSelectContext } from '../lib/menu-item-select-context.js';
import { toolbarDisabledTooltip } from '../lib/toolbar-disabled-tooltip.js';
import { withToolbarIconProps } from '../lib/with-toolbar-icon-props.js';
import type { DimeActionToolbarMenuItem as MenuItem } from './DimeActionToolbar.types.js';
import { DimeActionToolbarMenuBody } from './DimeActionToolbarMenuBody.js';

export function DimeActionToolbarMenuItem({ item }: Readonly<{ item: MenuItem }>) {
  const permitted = isToolbarItemAllowed(item);
  const disabled = Boolean(item.disabled) || !permitted;
  const { markItemSelected, isItemSelected } = React.useContext(MenuItemSelectContext);
  const reason = toolbarDisabledTooltip({
    permitted,
    disabled: item.disabled,
    disabledReason: item.disabledReason,
    deniedMessage: item.deniedMessage,
  });
  const reasonId = React.useId();
  const labelId = React.useId();
  const descriptionId = React.useId();
  const explanation = disabled ? reason : undefined;
  const describedBy =
    [item.description ? descriptionId : null, explanation ? reasonId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  // The spoken name is one string, since accessible-name computation trims the space between separate text nodes.
  const label = item.marker ? (
    <span className="inline-flex items-center gap-1">
      {withToolbarIconProps(item.marker.icon, 'size-3.5')}
      <span className="sr-only">{`${item.marker.label}: ${item.label}`}</span>
      <span aria-hidden>{item.label}</span>
    </span>
  ) : (
    item.label
  );

  if (item.submenu && !disabled) {
    return (
      <DimeDropdownMenuSub>
        <DimeDropdownMenuSubTrigger textValue={item.label}>
          {item.icon ? withToolbarIconProps(item.icon) : null}
          {label}
        </DimeDropdownMenuSubTrigger>
        <DimeActionToolbarMenuBody
          sub
          items={item.submenu}
          label={item.submenuLabel}
          search={item.submenuSearch}
        />
      </DimeDropdownMenuSub>
    );
  }

  const menuItem = (
    // Radix drops a `disabled` item from roving focus, so a disabled item stays focusable and inert to keep its reason reachable by keyboard.
    <DimeDropdownMenuItem
      textValue={item.label}
      aria-disabled={disabled || undefined}
      data-disabled={disabled ? '' : undefined}
      aria-labelledby={item.description ? labelId : undefined}
      aria-describedby={describedBy}
      onSelect={(event) => {
        if (disabled) {
          event.preventDefault();
          return;
        }
        markItemSelected();
        item.onSelect?.();
      }}
      onPointerLeave={(event) => {
        // Radix refocuses the menu when the pointer leaves an item, which would blur a dialog that just opened under it.
        if (isItemSelected()) event.preventDefault();
      }}
    >
      {item.icon ? withToolbarIconProps(item.icon) : null}
      {item.description ? (
        <span className="flex min-w-0 flex-col">
          <span id={labelId}>{label}</span>
          <span id={descriptionId} className="truncate text-xs text-muted-foreground">
            {item.description}
          </span>
        </span>
      ) : (
        label
      )}
    </DimeDropdownMenuItem>
  );
  if (!explanation) return menuItem;
  return (
    <>
      <DisabledTooltip tooltip={explanation} side="right" className="block" focusable={false}>
        {menuItem}
      </DisabledTooltip>
      <span id={reasonId} hidden>
        {explanation}
      </span>
    </>
  );
}
